import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  planilhaService,
  AdminLog,
  PlanilhaDeletada,
} from "../../services/planilhaService";

import { FiUnlock, FiList, FiExternalLink } from "react-icons/fi";
import { useFeedback } from "../../contexts/FeedbackContext";

type Tab = "logs" | "deletadas" | "locks";

interface ActiveLock {
  planilhaId: string;
  usuarioNome: string;
  adquiridoEm: string;
  expiraEm: string;
  planilha?: { evento: string; clienteNome: string };
}

export default function AdminPanel() {
  const { confirm, showSuccess, showError } = useFeedback();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("logs");

  // Logs
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsFilter, setLogsFilter] = useState({
    tipoAcao: "",
    planilhaId: "",
  });

  // Deletadas
  const [deletadas, setDeletadas] = useState<PlanilhaDeletada[]>([]);
  const [deletadasLoading, setDeletadasLoading] = useState(false);
  const [deletadasPage, setDeletadasPage] = useState(1);
  const [deletadasTotalPages, setDeletadasTotalPages] = useState(1);

  // Locks ativos
  const [locks, setLocks] = useState<ActiveLock[]>([]);
  const [locksLoading, setLocksLoading] = useState(false);

  useEffect(() => {
    if (tab === "logs") loadLogs();
  }, [tab, logsPage, logsFilter]);

  useEffect(() => {
    if (tab === "deletadas") loadDeletadas();
  }, [tab, deletadasPage]);

  useEffect(() => {
    if (tab === "locks") loadLocks();
  }, [tab]);

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await planilhaService.getAdminLogs({
        tipoAcao: logsFilter.tipoAcao || undefined,
        planilhaId: logsFilter.planilhaId || undefined,
        page: logsPage,
        pageSize: 25,
      });
      setLogs(res.items ?? []);
      setLogsTotalPages(res.pagination?.totalPages ?? 1);
    } catch {
      showError("Erro ao carregar logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const loadDeletadas = async () => {
    setDeletadasLoading(true);
    try {
      const res = await planilhaService.getDeletedPlanilhas({
        page: deletadasPage,
        pageSize: 20,
      });
      setDeletadas(res.items ?? []);
      setDeletadasTotalPages(res.pagination?.totalPages ?? 1);
    } catch {
      showError("Erro ao carregar planilhas excluídas");
    } finally {
      setDeletadasLoading(false);
    }
  };

  const loadLocks = async () => {
    setLocksLoading(true);
    try {
      const res = await planilhaService.getAdminLocks();
      setLocks(res as ActiveLock[]);
    } catch {
      showError("Erro ao carregar locks ativos");
    } finally {
      setLocksLoading(false);
    }
  };

  const handleForceUnlock = async (planilhaId: string, nome: string) => {
    const ok = await confirm({
      title: "Forçar desbloqueio",
      message: `Deseja forçar o desbloqueio da planilha "${nome}"?`,
      confirmText: "Desbloquear",
      cancelText: "Cancelar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await planilhaService.forceUnlock(planilhaId);
      showSuccess("Planilha desbloqueada com sucesso");
      loadLocks();
    } catch {
      showError("Erro ao desbloquear planilha");
    }
  };

  const tipoLabels: Record<string, string> = {
    criacao: "Criação",
    atualizacao_planilha: "Atualização",
    exclusao_planilha: "Exclusão",
    desbloqueio_forcado_admin: "Desbloqueio Admin",
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <FiList className="h-6 w-6 text-primary" />
        Painel Administrativo
      </h1>

      {/* Tabs */}
      <div className="tabs tabs-boxed mb-6">
        <button
          className={`tab ${tab === "logs" ? "tab-active" : ""}`}
          onClick={() => setTab("logs")}
        >
          Logs de Atividade
        </button>
        <button
          className={`tab ${tab === "deletadas" ? "tab-active" : ""}`}
          onClick={() => setTab("deletadas")}
        >
          Planilhas Excluídas
        </button>
        <button
          className={`tab ${tab === "locks" ? "tab-active" : ""}`}
          onClick={() => setTab("locks")}
        >
          Locks Ativos
          {locks.length > 0 && (
            <span className="badge badge-sm badge-warning ml-1">
              {locks.length}
            </span>
          )}
        </button>
      </div>

      {/* LOGS TAB */}
      {tab === "logs" && (
        <div>
          <div className="flex gap-3 mb-4 flex-wrap">
            <input
              className="input input-bordered input-sm w-48"
              placeholder="Filtrar por tipo..."
              value={logsFilter.tipoAcao}
              onChange={(e) => {
                setLogsFilter((f) => ({ ...f, tipoAcao: e.target.value }));
                setLogsPage(1);
              }}
            />
            <input
              className="input input-bordered input-sm w-56"
              placeholder="Filtrar por ID da planilha..."
              value={logsFilter.planilhaId}
              onChange={(e) => {
                setLogsFilter((f) => ({ ...f, planilhaId: e.target.value }));
                setLogsPage(1);
              }}
            />
          </div>
          {logsLoading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center text-base-content/60 py-12">
              Nenhum log encontrado.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-base-300">
              <table className="table table-zebra text-sm">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Usuário</th>
                    <th>Planilha</th>
                    <th>Observações</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td className="whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString("pt-BR")}
                      </td>
                      <td>
                        <span className="badge badge-outline text-xs">
                          {tipoLabels[log.tipoAlteracao] ?? log.tipoAlteracao}
                        </span>
                      </td>
                      <td>{log.usuario?.nome || log.usuario?.email || "-"}</td>
                      <td>
                        {log.planilha ? (
                          <div>
                            <div className="font-medium text-sm">
                              {log.planilha.evento}
                            </div>
                            <div className="text-xs text-base-content/50">
                              {log.planilha.clienteNome}
                            </div>
                          </div>
                        ) : (
                          <span className="font-mono text-xs text-base-content/40">
                            {log.planilhaId.substring(0, 8)}…
                          </span>
                        )}
                      </td>
                      <td className="max-w-xs text-xs text-base-content/70">
                        {log.observacoes || "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-xs"
                          title="Abrir planilha"
                          onClick={() =>
                            navigate(`/planilhas/${log.planilhaId}`)
                          }
                        >
                          <FiExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {logsTotalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                className="btn btn-sm"
                disabled={logsPage <= 1}
                onClick={() => setLogsPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span className="btn btn-sm btn-disabled">
                {logsPage} / {logsTotalPages}
              </span>
              <button
                className="btn btn-sm"
                disabled={logsPage >= logsTotalPages}
                onClick={() => setLogsPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      )}

      {/* DELETADAS TAB */}
      {tab === "deletadas" && (
        <div>
          {deletadasLoading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : deletadas.length === 0 ? (
            <p className="text-center text-base-content/60 py-12">
              Nenhuma planilha excluída.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-base-300">
              <table className="table table-zebra text-sm">
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th>Cliente</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Excluído em</th>
                    <th>Excluído por</th>
                  </tr>
                </thead>
                <tbody>
                  {deletadas.map((p) => (
                    <tr key={p.id}>
                      <td className="font-medium">{p.evento}</td>
                      <td>{p.clienteNome}</td>
                      <td>
                        <span className="badge badge-outline">{p.status}</span>
                      </td>
                      <td>
                        {new Date(p.createdAt).toLocaleDateString("pt-BR")}
                      </td>
                      <td>
                        {p.deletedAt
                          ? new Date(p.deletedAt).toLocaleString("pt-BR")
                          : "-"}
                      </td>
                      <td>{p.deletedBy || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {deletadasTotalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                className="btn btn-sm"
                disabled={deletadasPage <= 1}
                onClick={() => setDeletadasPage((p) => p - 1)}
              >
                Anterior
              </button>
              <span className="btn btn-sm btn-disabled">
                {deletadasPage} / {deletadasTotalPages}
              </span>
              <button
                className="btn btn-sm"
                disabled={deletadasPage >= deletadasTotalPages}
                onClick={() => setDeletadasPage((p) => p + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      )}

      {/* LOCKS TAB */}
      {tab === "locks" && (
        <div>
          <div className="flex justify-end mb-3">
            <button
              className="btn btn-sm btn-ghost"
              onClick={loadLocks}
              disabled={locksLoading}
            >
              Atualizar
            </button>
          </div>
          {locksLoading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : locks.length === 0 ? (
            <p className="text-center text-base-content/60 py-12">
              Nenhum lock ativo no momento.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-base-300">
              <table className="table table-zebra text-sm">
                <thead>
                  <tr>
                    <th>Planilha</th>
                    <th>Usuário</th>
                    <th>Adquirido em</th>
                    <th>Expira em</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {locks.map((lock) => (
                    <tr key={lock.planilhaId}>
                      <td>
                        <div className="font-medium">
                          {lock.planilha?.evento ||
                            lock.planilhaId.substring(0, 8) + "…"}
                        </div>
                        <div className="text-xs text-base-content/50">
                          {lock.planilha?.clienteNome}
                        </div>
                      </td>
                      <td>{lock.usuarioNome}</td>
                      <td>
                        {new Date(lock.adquiridoEm).toLocaleString("pt-BR")}
                      </td>
                      <td>{new Date(lock.expiraEm).toLocaleString("pt-BR")}</td>
                      <td>
                        <button
                          className="btn btn-error btn-sm"
                          title="Forçar desbloqueio"
                          onClick={() =>
                            handleForceUnlock(
                              lock.planilhaId,
                              lock.planilha?.evento ?? lock.planilhaId,
                            )
                          }
                        >
                          <FiUnlock className="h-4 w-4" />
                          Desbloquear
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
